import type {
    ComponentProps,
    ReactNode,
} from 'react'

import {
    Header as AriaHeader,
    Menu as AriaMenu,
    MenuItem as AriaMenuItem,
    MenuSection as AriaMenuSection,
    MenuTrigger as AriaMenuTrigger,
    Popover as AriaPopover,
    Separator as AriaSeparator,
    Text as AriaText,
    type MenuItemProps as AriaMenuItemProps,
    type MenuProps as AriaMenuProps,
    type PopoverProps as AriaPopoverProps,
} from 'react-aria-components'

import {
    cva,
    type VariantProps,
} from 'class-variance-authority'

import { cn } from '@/shared/lib/cn'

export const MenuTrigger =
    AriaMenuTrigger

const menuItemVariants = cva(
    [
        'group flex cursor-pointer select-none',
        'items-center gap-3',
        'rounded-lg px-2.5 py-2.5',
        'outline-none',

        'text-[var(--color-foreground)]',

        'transition-colors duration-150',

        'data-hovered:bg-[var(--color-surface-muted)]',
        'data-focused:bg-[var(--color-surface-muted)]',
        'data-pressed:bg-[var(--color-surface-muted)]',

        'data-disabled:pointer-events-none',
        'data-disabled:opacity-50',
    ],
    {
        variants: {
            variant: {
                default: '',

                danger: [
                    'text-[var(--color-danger)]',

                    'data-hovered:bg-[var(--toast-error-background)]',
                    'data-focused:bg-[var(--toast-error-background)]',

                    'data-hovered:text-[var(--color-danger)]',
                    'data-focused:text-[var(--color-danger)]',
                ],
            },
        },

        defaultVariants: {
            variant: 'default',
        },
    },
)

export interface MenuPopoverProps
    extends Omit<
        AriaPopoverProps,
        'className'
    > {
    className?: string
}

export function MenuPopover({
    className,
    ...props
}: MenuPopoverProps) {
    return (
        <AriaPopover
            {...props}
            className={cn(
                [
                    'z-50',

                    'w-[min(20rem,calc(100vw-2rem))]',
                    'overflow-hidden',

                    'rounded-xl',
                    'border border-(--color-border)',

                    'bg-(--color-surface)',
                    'text-(--color-foreground)',

                    'shadow-[var(--shadow-popover)]',
                    'outline-none',

                    'origin-(--trigger-anchor-point)',

                    'transition-[opacity,transform]',
                    'duration-150 ease-out',

                    'data-entering:scale-95',
                    'data-entering:opacity-0',

                    'data-exiting:scale-95',
                    'data-exiting:opacity-0',
                ],
                className,
            )}
        />
    )
}

export interface MenuProps<
    T extends object,
> extends Omit<
    AriaMenuProps<T>,
    'className'
> {
    className?: string
}

export function Menu<
    T extends object,
>({
    className,
    ...props
}: MenuProps<T>) {
    return (
        <AriaMenu
            {...props}
            className={cn(
                'p-1.5 outline-none',
                className,
            )}
        />
    )
}

export interface MenuItemProps
    extends Omit<
        AriaMenuItemProps,
        'className'
    >,
    VariantProps<
        typeof menuItemVariants
    > {
    className?: string
}

export function MenuItem({
    variant,
    className,
    ...props
}: MenuItemProps) {
    return (
        <AriaMenuItem
            {...props}
            className={cn(
                menuItemVariants({
                    variant,
                }),
                className,
            )}
        />
    )
}

export const MenuSection =
    AriaMenuSection

export interface MenuHeaderProps
    extends Omit<
        ComponentProps<
            typeof AriaHeader
        >,
        'className'
    > {
    className?: string
}

export function MenuHeader({
    className,
    ...props
}: MenuHeaderProps) {
    return (
        <AriaHeader
            {...props}
            className={cn(
                'px-2 pb-2 pt-1',
                className,
            )}
        />
    )
}

export interface MenuSeparatorProps
    extends Omit<
        ComponentProps<
            typeof AriaSeparator
        >,
        'className'
    > {
    className?: string
}

export function MenuSeparator({
    className,
    ...props
}: MenuSeparatorProps) {
    return (
        <AriaSeparator
            {...props}
            className={cn(
                'mx-2 my-1 h-px bg-(--color-border)',
                className,
            )}
        />
    )
}

export interface MenuItemIconProps {
    children: ReactNode
    className?: string
}

export function MenuItemIcon({
    children,
    className,
}: MenuItemIconProps) {
    return (
        <span
            aria-hidden="true"
            className={cn(
                [
                    'flex h-8 w-8 shrink-0',
                    'items-center justify-center',
                    'rounded-lg',

                    'bg-(--color-surface-muted)',
                    'text-(--color-muted-foreground)',

                    'group-data-focused:text-(--color-foreground)',
                ],
                className,
            )}
        >
            {children}
        </span>
    )
}

export interface MenuItemLabelProps {
    children: ReactNode
    className?: string
}

export function MenuItemLabel({
    children,
    className,
}: MenuItemLabelProps) {
    return (
        <AriaText
            slot="label"
            className={cn(
                'block truncate text-sm font-medium',
                className,
            )}
        >
            {children}
        </AriaText>
    )
}

export interface MenuItemDescriptionProps {
    children: ReactNode
    className?: string
}

export function MenuItemDescription({
    children,
    className,
}: MenuItemDescriptionProps) {
    return (
        <AriaText
            slot="description"
            className={cn(
                [
                    'mt-0.5 block truncate',
                    'text-xs',
                    'text-(--color-muted-foreground)',
                ],
                className,
            )}
        >
            {children}
        </AriaText>
    )
}

export interface MenuItemContentProps {
    children: ReactNode
    className?: string
}

export function MenuItemContent({
    children,
    className,
}: MenuItemContentProps) {
    return (
        <span
            className={cn(
                'min-w-0 flex-1',
                className,
            )}
        >
            {children}
        </span>
    )
}
